import LinkCustomField from './LinkCustomField';
import PlainTextCustomField from './PlainTextCustomField';
import SelectConstantCustomField from './SelectConstantCustomField';
import SelectMultipleField from './SelectMultipleField';
import UrlCustomFieldOriginal from './UrlCustomField';
import RecordCustomField from './RecordCustomField';
import { UrlCustomFieldSet } from './UrlCustomFieldSet/UrlCustomFieldSet';
import { UrlCustomField } from './UrlCustomFieldSet/UrlCustomField';
import { UrlCustomFieldLink } from './UrlCustomFieldSet/UrlCustomFieldLink';
import { UrlCustomFieldLinkIcon } from './UrlCustomFieldSet/UrlCustomFieldLinkIcon';
import NumberCustomField from './NumberCustomField';
import { currentFlavorSupportsTeams } from 'javascripts/util/currentFlavorSupportsTeams';

const NEVER_SUPPORTED_CUSTOM_FIELDS = [
  'AttachmentField',
  'LinkMasterDetail',
  'NoteField',
  'RecordsField',
];

// These custom fields are supported when rendered on the server, but they are not currently
// supported when rendered via GQL (GQL does not currently return data for them).
// They can all be disabled by passing `hideRailsRenderingOptions`.
const SOMETIMES_SUPPORTED_CUSTOM_FIELDS = [
  'CompetitorsField',
  'CreativeBriefsField',
  'EpicsField',
  'EquationField',
  'FeaturesField',
  'IdeaCategoriesField',
  'IdeaOrganizationsField',
  'IdeasField',
  'InitiativesField',
  'LinkMany',
  'LinkOne',
  'PersonasField',
  'ProjectsField',
  'ReleasesField',
  'StrategicImperativesField',
  'UsersField',
];

const CUSTOM_FIELDS = {
  DateField: PlainTextCustomField,
  LinkMany: LinkCustomField,
  LinkOne: LinkCustomField,
  NumberField: NumberCustomField,
  ScorecardField: PlainTextCustomField,
  SelectConstant: SelectConstantCustomField,
  SelectEditable: PlainTextCustomField,
  SelectMultipleConstant: SelectMultipleField,
  SelectMultipleEditable: SelectMultipleField,
  TextField: PlainTextCustomField,
  UrlField: UrlCustomFieldOriginal,

  // These fields are not supported in Develop yet, but we need to show examples
  // for Roadmap layouts
  CompetitorsField: RecordCustomField,
  CreativeBriefsField: RecordCustomField,
  EpicsField: RecordCustomField,
  EquationField: PlainTextCustomField,
  FeaturesField: RecordCustomField,
  IdeaCategoriesField: RecordCustomField,
  IdeaOrganizationsField: RecordCustomField,
  IdeasField: RecordCustomField,
  InitiativesField: RecordCustomField,
  PersonasField: RecordCustomField,
  ProjectsField: RecordCustomField,
  ReleasesField: RecordCustomField,
  StrategicImperativesField: RecordCustomField,
  UsersField: RecordCustomField,
};

const CUSTOM_FIELD_SETS = {
  UrlField: UrlCustomFieldSet,
};

const CUSTOM_FIELD_SET_FIELDS = {
  UrlCustomField: UrlCustomField,
  UrlCustomFieldLink: UrlCustomFieldLink,
  UrlCustomFieldLinkIcon: UrlCustomFieldLinkIcon,
};

export function buildCustomField(
  customFieldDefinition,
  hideRailsRenderingOptions = false,
  customFieldSetField: string | null | undefined = null
) {
  // It's possible the custom field was deleted, but the field is still in the layout
  if (!customFieldDefinition) {
    return null;
  }

  let CustomFieldClass;
  if (customFieldSetField) {
    CustomFieldClass = CUSTOM_FIELD_SET_FIELDS[customFieldSetField];
    return new CustomFieldClass(customFieldDefinition);
  }

  const { type } = customFieldDefinition;

  // Some fields are just not supported on cards at all
  if (NEVER_SUPPORTED_CUSTOM_FIELDS.includes(type)) {
    return null;
  }

  // Some fields are not supported in Develop and a few other places.
  if (
    (currentFlavorSupportsTeams() ||
      window.currentProject?.isTeam ||
      hideRailsRenderingOptions) &&
    SOMETIMES_SUPPORTED_CUSTOM_FIELDS.includes(type)
  ) {
    return null;
  }

  if (!(type in CUSTOM_FIELDS)) {
    return null;
  }

  CustomFieldClass = CUSTOM_FIELDS[type];

  return new CustomFieldClass(customFieldDefinition);
}

export function buildCustomFieldSet(
  customFieldDefinition,
  hideRailsRenderingOptions = false
) {
  // It's possible the custom field was deleted, but the field is still in the layout
  if (!customFieldDefinition) {
    return null;
  }

  const { type } = customFieldDefinition;

  // Some fields are just not supported on cards at all
  if (NEVER_SUPPORTED_CUSTOM_FIELDS.includes(type)) {
    return null;
  }

  // Some fields are not supported in Develop and a few other places.
  if (
    (currentFlavorSupportsTeams() ||
      window.currentProject?.isTeam ||
      hideRailsRenderingOptions) &&
    SOMETIMES_SUPPORTED_CUSTOM_FIELDS.includes(type)
  ) {
    return null;
  }

  if (!(type in CUSTOM_FIELD_SETS)) {
    return null;
  }

  const CustomFieldClass = CUSTOM_FIELD_SETS[type];

  return new CustomFieldClass(customFieldDefinition);
}
